import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Calendar, ExternalLink, Building2, CreditCard, FileText, Calculator, Printer, Download, Mail, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { api } from '../../core/apiClient';
import apiClient from '../../core/apiClient';
import { useAuth } from '../../auth/AuthContext';
import { formatCurrency } from '../../lib/utils';
import { SHIPPING_METHODS } from './constants';

interface SupplierContact {
  name: string;
  position?: string;
  phone?: string;
  email?: string;
  isPrimary?: boolean;
}

interface Supplier {
  id: string;
  code: string;
  name: string;
  businessName: string;
  tradeName?: string;
  shippingMethod?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  nif?: string;
  phone?: string;
  email?: string;
  paymentTerms?: number;
  paymentMethod?: string;
  bankAccount?: string;
  contacts?: SupplierContact[];
  // Per filtrar el diàleg "Sol·licitar pressupost" pels proveïdors de la
  // mateixa activitat que el proveïdor triat a la comanda (mateix criteri
  // que PurchaseOrderDetail.tsx).
  activitatId?: number | null;
  activitat?: { id: number; name: string } | null;
  supplierType?: string;
}

interface ProjectOrder {
  id: string;
  code: string;
  name: string;
  status: string;
  brandId?: string; // Brand associated with the project
  brand?: Brand;
}

interface CompanyUser {
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  email?: string;
}

// Línia del desglose de l'escandall vinculat a la línia de pressupost de la
// qual ve la OT (mateixa forma que NodeDetailLine a BudgetForm.tsx) — es fa
// servir nomes per pre-omplir els materials de la comanda.
interface EscandallDetailLine {
  childType?: string;
  nature?: string;
  description?: string;
  unit?: string;
  quantity?: number;
  dimensionA?: number;
  dimensionB?: number;
  dimensionH?: number;
  resolvedUnitCost?: number;
  pesTotal?: number;
  referencia?: string;
}

interface WorkOrder {
  id: string;
  order_number: string;
  sequence_number?: number;
  name?: string;
  type?: string;
  status?: string;
  expedient_number?: number;
  project_name?: string;
  projectId?: string;
  project?: ProjectOrder;
  projectBudgetLineEscandallLines?: EscandallDetailLine[];
}

function escandallLineMeasurement(l: EscandallDetailLine): number {
  const qty = Number(l.quantity) || 0;
  const a = Number(l.dimensionA) || 0;
  const b = Number(l.dimensionB) || 0;
  const h = Number(l.dimensionH) || 0;
  if (a > 0 && b > 0 && h > 0) return qty * a * b * h;
  if (a > 0 && b > 0)          return qty * a * b;
  if (a > 0)                   return qty * a;
  return qty;
}

interface Brand {
  id: string;
  code: string;
  name: string;
  logoUrl?: string;
  primaryColor?: string;
}

interface Material {
  id: string;
  code: string;
  name: string;
  unit: string;
  costPerUnit: number;
  weightKgPerUnit?: number;
  measurementFactor?: number;
  priceFactor?: number;
  purchaseCost?: number;
  purchaseCostUnit?: string;
}

interface PurchaseOrderLine {
  id?: string;
  lineNumber: number;
  element?: number;
  quantity: number;
  unitType: string;
  description: string;
  supplierCode?: string;
  purchasePrice: number;
  purchaseCostUnit?: string;
  weightKg: number;
  weightKgPerUnit: number;
  measurementFactor: number;
  // F.Amidament propi de l'element (per recalcular measurementFactor quan canvia Ut.)
  elementMeasurementFactor: number;
  // Factor Preu/UA de l'element — Preu/Ut = F.Amidament × Factor Preu/UA × Cost Compra.
  elementPriceFactor: number;
  unitPrice: number;
  discountPercent: number;
  lineTotal: number;
  materialId?: string;
}

const parseNum = (s: string | number | undefined): number => {
  if (typeof s === 'number') return isNaN(s) ? 0 : s;
  return parseFloat(String(s ?? '0').replace(',', '.')) || 0;
};

// Arrodoneix a `decimals` decimals abans de guardar-ho a l'estat — evita que
// errors de coma flotant en cadena (ex: 12.339999999999999) es mostrin quan
// l'usuari fa focus a la cel·la per editar-la (veure numInput més avall).
const round = (v: number, decimals = 2): number => {
  const factor = 10 ** decimals;
  return Math.round(v * factor) / factor;
};

const fmt3 = (v: number | undefined) =>
  Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const fmt2 = (v: number | undefined) =>
  Number(v || 0).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const emptyLine: PurchaseOrderLine = {
  lineNumber: 1,
  element: 0,
  quantity: 0,
  unitType: 'Ut',
  description: '',
  supplierCode: '',
  purchasePrice: 0,
  purchaseCostUnit: '',
  weightKg: 0,
  weightKgPerUnit: 0,
  measurementFactor: 1,
  elementMeasurementFactor: 1,
  elementPriceFactor: 1,
  unitPrice: 0,
  discountPercent: 0,
  lineTotal: 0,
};

export default function CreatePurchaseOrder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const initialSupplierId: string | undefined = (location.state as any)?.supplierId;
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [projects, setProjects] = useState<ProjectOrder[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [metodesPagament, setMetodesPagament] = useState<{ id: number; name: string }[]>([]);
  const [materialCache, setMaterialCache] = useState<Record<string, Material>>({});
  const [materialSuggestions, setMaterialSuggestions] = useState<Material[]>([]);
  const materialSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supplierInputRef = useRef<HTMLInputElement>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [supplierContacts, setSupplierContacts] = useState<SupplierContact[]>([]);
  const [lines, setLines] = useState<PurchaseOrderLine[]>([{ ...emptyLine }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Autocomplete state
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [lineSearches, setLineSearches] = useState<Record<number, string>>({});
  const [lineOpenIdx, setLineOpenIdx] = useState<number | null>(null);
  const [lineDropdownPos, setLineDropdownPos] = useState<{ top: number; bottom: number; left: number; width: number } | null>(null);

  // Numeric cell editing state (raw string while user types)
  const [editCells, setEditCells] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    supplierId: '',
    workOrderId: '',
    projectId: '',
    brandId: '',
    orderDate: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: '',
    persona: '',
    referenceUserId: '',
    shippingMethod: '',
    deliveryTerm: '',
    deliveryLocation: "Carrer del Cobalt, 82, l'Hospitalet de Llobregat, 08907, Barcelona",
    paymentMethod: '',
    observations: '',
    taxRate: 21,
    currency: 'EUR',
  });

  useEffect(() => {
    fetchSuppliers();
    fetchProjects();
    fetchWorkOrders();
    fetchBrands();
    fetchCompanyUsers();
    fetchMetodesPagament();
  }, []);

  // Load order once per id (NOT on every suppliers change to avoid overwriting user edits)
  useEffect(() => {
    if (id) {
      loadPurchaseOrder(id);
    }
  }, [id]);

  // When suppliers list arrives, resolve selectedSupplier from formData.supplierId
  useEffect(() => {
    if (suppliers.length > 0 && formData.supplierId) {
      const supplier = suppliers.find(s => s.id === formData.supplierId);
      if (supplier) setSelectedSupplier(supplier);
    }
  }, [suppliers, formData.supplierId]);

  // Autofocus al camp proveïdor en crear nova comanda (no si ve preseleccionat)
  useEffect(() => {
    if (!id && !initialSupplierId && suppliers.length > 0) {
      supplierInputRef.current?.focus();
    }
  }, [id, initialSupplierId, suppliers.length]);

  // Preseleccionar proveïdor quan ve d'un proveïdor extern (des de Suppliers)
  useEffect(() => {
    if (initialSupplierId && suppliers.length > 0) {
      handleSupplierChange(initialSupplierId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSupplierId, suppliers.length]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Supplier[] }>('/suppliers?limit=3000');
      const suppliers = response.data || [];
      setSuppliers(Array.isArray(suppliers) ? suppliers : []);
    } catch (err) {
      console.error('[v0] Error fetching suppliers:', err);
      setSuppliers([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get<{ success: boolean; data: ProjectOrder[] }>('/taller/projects');
      const projects = response.data || [];
      setProjects(Array.isArray(projects) ? projects : []);
    } catch (err) {
      console.error('[v0] Error fetching projects:', err);
      setProjects([]);
    }
  };

  const searchMaterials = useCallback(async (q: string) => {
    try {
      const res = await api.get<{ success: boolean; data: Material[] }>(
        `/simple-materials/search?q=${encodeURIComponent(q)}&limit=30`
      );
      const results = res.data ?? [];
      setMaterialSuggestions(results);
      setMaterialCache(prev => {
        const next = { ...prev };
        results.forEach(m => { next[m.id] = m; });
        return next;
      });
    } catch {
      setMaterialSuggestions([]);
    }
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await api.get<{ success: boolean; data: Brand[] }>('/brands');
      const brands = response.data || [];
      setBrands(Array.isArray(brands) ? brands : []);
    } catch (err) {
      console.error('[v0] Error fetching brands:', err);
      setBrands([]);
    }
  };

  // Marca EQUIRO per defecte en pedidos nuevos
  useEffect(() => {
    if (!id && brands.length > 0 && !formData.brandId) {
      const equiro = brands.find(b => b.code === 'EQUIRO' || b.name === 'EQUIRO');
      if (equiro) setFormData(prev => ({ ...prev, brandId: equiro.id }));
    }
  }, [brands, id]);

  const fetchCompanyUsers = async () => {
    try {
      const response = await api.get<{ success: boolean; data: CompanyUser[] }>('/auth/users');
      const users = response.data || [];
      setCompanyUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error('[v0] Error fetching company users:', err);
      setCompanyUsers([]);
    }
  };

  const fetchMetodesPagament = async () => {
    try {
      const response = await api.get<{ success: boolean; data: { id: number; name: string; isActive: number }[] }>('/admin/metodes-pagament');
      const list = (response.data || []).filter((m) => m.isActive);
      setMetodesPagament(list);
    } catch (err) {
      console.error('[v0] Error fetching metodes de pagament:', err);
      setMetodesPagament([]);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      // OT's de tipus "Materials" (MAT) i "Serveis/Subcontractes" (SRV) — son les
      // uniques que te sentit vincular a una comanda de compra. El backend nomes
      // filtra per un sol `type` alhora, per aixo es fan dues crides (2026-10-07).
      const [matRes, srvRes] = await Promise.all([
        api.get<{ success: boolean; data: any[] }>('/work-orders?type=MAT'),
        api.get<{ success: boolean; data: any[] }>('/work-orders?type=SRV'),
      ]);
      const raw = [...(matRes.data || []), ...(srvRes.data || [])];
      // L'endpoint canonic retorna les entitats en camelCase (amb project
      // niat), no el format snake_case pla que aquest formulari esperava.
      const workOrders: WorkOrder[] = (Array.isArray(raw) ? raw : []).map((wo: any) => ({
        id: wo.id,
        order_number: wo.orderNumber || wo.order_number || '',
        sequence_number: wo.sequenceNumber ?? wo.sequence_number,
        name: wo.name,
        type: wo.type,
        status: wo.status,
        expedient_number: wo.project?.expedientNumber ?? wo.expedient_number,
        project_name: wo.project?.name ?? wo.project_name,
        projectId: wo.projectId,
        project: wo.project,
        projectBudgetLineEscandallLines: wo.projectBudgetLine?.escandallLines,
      }));
      // Ordenades per numero d'OT (no agrupades per tipus, que es com queden si
      // es concatenen tal qual les dues crides MAT/SRV).
      workOrders.sort((a, b) => a.order_number.localeCompare(b.order_number, undefined, { numeric: true }));
      setWorkOrders(workOrders);
    } catch (err) {
      console.error('[v0] Error fetching work orders:', err);
      setWorkOrders([]);
    }
  };

  const handleWorkOrderChange = async (workOrderId: string) => {
    setFormData(prev => ({ ...prev, workOrderId }));

    // Auto-fill brand from work order's project if available
    if (workOrderId) {
      const selectedWO = workOrders.find(wo => wo.id === workOrderId);
      if (selectedWO?.project?.brandId) {
        setFormData(prev => ({ ...prev, brandId: selectedWO.project!.brandId }));
      }

      // Pre-omplir les línies de la comanda amb els materials/serveis de l'escandall
      // d'aquesta OT (Planificació → Compres) — editable despres, com el prefill des del
      // mestre de materials. Nomes si l'usuari encara no ha començat a omplir línies.
      //
      // Es demana el detall via API (/work-orders/:id/detail-lines) en lloc de fer servir
      // `projectBudgetLineEscandallLines` (precarregat a la llista, nomes el `projectBudgetLine`
      // legacy/singular) — una OT agrupada pot referenciar VÀRIES línies de pressupost
      // (groupedBudgetLines) i l'endpoint ja les resol totes; abans nomes s'importava la
      // primera, deixant fora la resta de línies previstes.
      let detailLines: EscandallDetailLine[] = [];
      try {
        const res = await api.get<{ success: boolean; data: EscandallDetailLine[] }>(`/work-orders/${workOrderId}/detail-lines`);
        detailLines = res.data || [];
      } catch (err) {
        console.error('[v0] Error fetching work order detail lines:', err);
      }

      // La naturalesa esperada depèn del tipus de la OT (MAT->MATERIAL, SRV->SERVEIS).
      const expectedNature = selectedWO?.type === 'SRV' ? 'SERVEIS' : 'MATERIAL';
      const materialLines = detailLines
        .filter((l) => l.childType === 'MATERIAL' && l.nature === expectedNature);
      const linesAreEmpty = lines.length <= 1 && !lines[0]?.description && !lines[0]?.quantity;
      if (materialLines.length > 0 && (linesAreEmpty || confirm(
        `Aquesta OT te ${materialLines.length} ${expectedNature === 'SERVEIS' ? 'serveis previstos' : 'materials previstos'} a l'escandall. Vols substituir les línies actuals per aquests?`
      ))) {
        setLines(materialLines.map((l, idx) => {
          const measurement = escandallLineMeasurement(l);
          const unitCost = Number(l.resolvedUnitCost || 0);
          return {
            ...emptyLine,
            lineNumber: idx + 1,
            quantity: measurement,
            unitType: l.unit || 'Ut',
            description: l.description || '',
            supplierCode: l.referencia || '',
            purchasePrice: unitCost,
            weightKg: Number(l.pesTotal || 0),
            weightKgPerUnit: measurement > 0 ? Number(l.pesTotal || 0) / measurement : 0,
            unitPrice: unitCost,
            lineTotal: measurement * unitCost,
          };
        }));
      }
    }
  };

  const loadPurchaseOrder = async (orderId: string) => {
    try {
      const response = await api.get<{ success: boolean; data: any }>(`/purchase-orders/${orderId}`);
      if (!response.data) {
        setError('No se pudo cargar la comanda');
        return;
      }

      const order = response.data;
      
      // Cargar contactes del proveedor (selectedSupplier se setea via useEffect([suppliers, formData.supplierId]))
      if (order.supplierId) {
        api.get<{ success: boolean; data: { contacts?: SupplierContact[] } }>(`/suppliers/${order.supplierId}`)
          .then((r) => setSupplierContacts(r.data?.contacts ?? []))
          .catch(() => {});
      }

      // Mapear datos del formulario
      setFormData(prev => ({
        ...prev,
        supplierId: order.supplierId || '',
        workOrderId: order.workOrderId || '',
        projectId: order.projectId || '',
        brandId: order.brandId || '',
        orderDate: order.orderDate ? order.orderDate.split('T')[0] : prev.orderDate,
        expectedDeliveryDate: order.expectedDeliveryDate ? order.expectedDeliveryDate.split('T')[0] : '',
        persona: order.persona || '',
        referenceUserId: order.referenceUserId || order.referenceUser?.id || '',
        shippingMethod: order.shippingMethod || '',
        deliveryTerm: order.deliveryTerm || '',
        deliveryLocation: order.deliveryLocation || '',
        paymentMethod: order.paymentMethod || '',
        observations: order.observations || '',
        taxRate: order.taxRate || 21,
        currency: order.currency || 'EUR',
      }));

      // Mapear líneas (cuidado: entidad usa unitType, no unitOfMeasure)
      console.log('[v0] loadPurchaseOrder lines raw:', JSON.stringify(order.lines));
      if (order.lines && Array.isArray(order.lines)) {
        const mappedLines = order.lines.map((line: any, idx: number) => ({
          id: line.id,
          lineNumber: idx + 1,
          materialId: line.materialId,
          description: line.description || '',
          quantity: Number(line.quantity) || 0,
          unitType: line.unitType || 'Ut',
          unitPrice: Number(line.unitPrice) || 0,
          discountPercent: Number(line.discountPercent) || 0,
          // lineTotal ja ve calculat correctament des del backend (unitPrice × (1-descompte%),
          // sense tornar a multiplicar per Ut.) — es llegeix tal qual, no es recalcula aqui.
          lineTotal: Number(line.lineTotal) || 0,
          element: 0,
          supplierCode: '',
          purchasePrice: Number(line.purchasePrice) || 0,
          purchaseCostUnit: line.purchaseCostUnit || '',
          weightKg: Number(line.weightKg) || 0,
          // measurementFactor/weightKgPerUnit ja es guarden a BD des d'aquest canvi — es
          // llegeixen tal qual. Nomes es reconstrueixen (per no perdre dades) en linies
          // antigues d'abans d'aquest canvi, on weightKgPerUnit encara no s'havia guardat.
          // Amidament reconstruit = Ut. (F.Amidament implicit=1 en linies antigues), perque
          // Pes Kg = Amidament × Pes/ut torni a donar el mateix pes total ja guardat.
          ...(() => {
            const savedWeightKgPerUnit = Number(line.weightKgPerUnit) || 0;
            const quantity = Number(line.quantity) || 0;
            const isLegacyLine = savedWeightKgPerUnit === 0 && Number(line.weightKg) > 0;
            const measurementFactor = isLegacyLine ? (quantity || 1) : (Number(line.measurementFactor) || 1);
            return {
              measurementFactor,
              weightKgPerUnit: isLegacyLine
                ? (quantity > 0 ? Number(line.weightKg) / quantity : Number(line.weightKg) || 0)
                : savedWeightKgPerUnit,
              // F.Amidament propi de l'element, derivat del total guardat, per si l'usuari canvia Ut.
              elementMeasurementFactor: quantity > 0 ? measurementFactor / quantity : measurementFactor,
              // Factor Preu/UA no es persisteix directament — es reconstrueix a partir de
              // unitPrice = Amidament × Factor Preu/UA × Preu de compra (tots guardats).
              elementPriceFactor: (() => {
                const savedUnitPrice = Number(line.unitPrice) || 0;
                const savedPurchasePrice = Number(line.purchasePrice) || 0;
                const denom = measurementFactor * savedPurchasePrice;
                return denom > 0 ? savedUnitPrice / denom : 1;
              })(),
            };
          })(),
        }));
        setLines(mappedLines.length > 0 ? mappedLines : [{ ...emptyLine }]);
      }
    } catch (err) {
      console.error('[v0] Error loading purchase order:', err);
      setError('Error en carregar la comanda');
    }
  };

  const handleSupplierChange = (supplierId: string) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setSelectedSupplier(supplier || null);
    
    // Preparar datos a actualizar
    const updateData: any = {
      supplierId,
    };

    // Autorrellenar Forma de Pagament desde el método de pago del proveedor
    if (supplier?.paymentMethod) {
      updateData.paymentMethod = supplier.paymentMethod;
    }

    // Autorrellenar días de pago si el proveedor los tiene informados
    if (supplier?.paymentTerms != null) {
      updateData.paymentDays = supplier.paymentTerms;
    }

    // Si el proveedor tiene shipping_method, rellena automáticamente (usuario puede cambiarla después)
    if (supplier?.shippingMethod) {
      updateData.shippingMethod = supplier.shippingMethod;
    }
    
    setFormData(prev => ({ ...prev, ...updateData, persona: '' }));

    // Carregar contactes complets del proveïdor
    setSupplierContacts([]);
    if (supplierId) {
      api.get<{ success: boolean; data: { contacts?: SupplierContact[] } }>(`/suppliers/${supplierId}`)
        .then((r) => setSupplierContacts(r.data?.contacts ?? []))
        .catch(() => {});
    }
  };

  const addLine = () => {
    setLines(prev => [...prev, { ...emptyLine, lineNumber: prev.length + 1 }]);
  };

  const removeLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const updateLine = (index: number, field: keyof PurchaseOrderLine, value: unknown) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Si canvia Ut., recalcula Amidament = Ut. × F.Amidament de l'element (nomes quan hi ha
      // un element vinculat) — aixi Amidament sempre reflecteix la Ut. actual.
      if (field === 'quantity') {
        const elementFactor = parseNum(updated[index].elementMeasurementFactor as number) || 1;
        updated[index].measurementFactor = parseNum(value as number) * elementFactor;
      }

      // Amidament (measurementFactor) ja inclou Ut. (Ut. × F.Amidament de l'element), per
      // tant Import i Pes Kg NO tornen a multiplicar per Ut. — ja hi és inclòs.
      // Import = Preu/Ut × Ut = Amidament × Factor Preu/UA × Preu de compra.
      const measurementFactor = parseNum(updated[index].measurementFactor as number);
      const elementPriceFactor = parseNum(updated[index].elementPriceFactor as number) || 1;
      const purchasePrice = parseNum(updated[index].purchasePrice as number);
      updated[index].unitPrice = round(measurementFactor * elementPriceFactor * purchasePrice);

      const price = parseNum(updated[index].unitPrice as number);
      const discount = parseNum(updated[index].discountPercent as number);
      updated[index].lineTotal = round(price * (1 - discount / 100));

      // Pes Kg = Amidament × Pes de l'element (weightKgPerUnit). Si no hi ha cap material
      // vinculat amb pes conegut, es deixa el valor tal com l'ha introduit l'usuari.
      const weightKgPerUnit = parseNum(updated[index].weightKgPerUnit as number);
      if (weightKgPerUnit > 0) {
        updated[index].weightKg = round(measurementFactor * weightKgPerUnit, 3);
      }

      return updated;
    });
  };

  const calculateTotals = () => {
    const subtotal = round(lines.reduce((sum, l) => sum + Number(l.lineTotal || 0), 0));
    const totalWeight = round(lines.reduce((sum, l) => sum + Number(l.weightKg || 0), 0), 3);
    const taxRate = Number(formData.taxRate) || 21;
    const taxAmount = round(subtotal * (taxRate / 100));
    const total = round(subtotal + taxAmount);
    return { subtotal, taxAmount, total, totalWeight };
  };

  const totals = calculateTotals();

  const numInput = (idx: number, field: keyof PurchaseOrderLine, extraClass = '') => {
    const key = `${idx}_${String(field)}`;
    const stored = editCells[key];
    const numVal = parseNum(lines[idx][field] as number);
    const fmt = field === 'weightKg' ? fmt3 : fmt2;
    return (
      <input
        type="text"
        inputMode="decimal"
        value={stored !== undefined ? stored : fmt(numVal)}
        onFocus={() => setEditCells(prev => ({
          ...prev,
          [key]: String(numVal || 0).replace('.', ','),
        }))}
        onChange={(e) => setEditCells(prev => ({ ...prev, [key]: e.target.value }))}
        onBlur={() => {
          const parsed = parseNum(editCells[key] ?? '');
          updateLine(idx, field, parsed);
          setEditCells(prev => { const n = { ...prev }; delete n[key]; return n; });
        }}
        className={`w-full h-7 text-xs text-right border border-input rounded px-1 bg-white focus:outline-none focus:ring-1 focus:ring-ring ${extraClass}`}
      />
    );
  };

  // saveOrder desa la comanda i retorna l'id (nou o existent) sense navegar
  // — handleSave l'usa per al flux normal (desar i tornar a la llista), i
  // handleConfirmOrder per assegurar-se que "Confirmar pedido" envia per
  // correu les dades que l'usuari acaba d'editar, no les de l'ultim desat.
  const saveOrder = async (): Promise<string | null> => {
    if (!formData.supplierId) {
      setError('Has de seleccionar un proveïdor');
      return null;
    }

    setError(null);

    try {
      const payload = {
        supplierId: formData.supplierId,
        expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
        taxRate: formData.taxRate ?? 21,
        currency: formData.currency || 'EUR',
        paymentDays: (formData as any).paymentDays ?? 30,
        shippingMethod: formData.shippingMethod,
        deliveryTerm: formData.deliveryTerm,
        deliveryLocation: formData.deliveryLocation,
        // A diferencia de shippingMethod/deliveryTerm/observations (text
        // lliure, on cal poder buidar-los), paymentMethod ve d'un catàleg —
        // si mai queda buit per error (p.ex. valor antic no reconegut al
        // desplegable) no s'ha de sobreescriure el que ja hi hagués guardat.
        paymentMethod: formData.paymentMethod || undefined,
        referenceUserId: formData.referenceUserId || undefined,
        persona: formData.persona || undefined,
        observations: formData.observations,
        projectId: formData.projectId || undefined,
        workOrderId: formData.workOrderId || undefined,
        brandId: formData.brandId || undefined,
        items: lines.map((l) => ({
          materialId: l.materialId || undefined,
          description: l.description,
          quantity: Number(l.quantity),
          unitOfMeasure: l.unitType || 'Ut',
          unitPrice: Number(l.unitPrice),
          discountPercent: Number(l.discountPercent) || 0,
          purchasePrice: Number(l.purchasePrice) || 0,
          purchaseCostUnit: l.purchaseCostUnit || undefined,
          weightKg: Number(l.weightKg) || 0,
          weightKgPerUnit: Number(l.weightKgPerUnit) || 0,
          measurementFactor: Number(l.measurementFactor) || 1,
        })),
      };

      if (id) {
        await api.put(`/purchase-orders/${id}`, payload);
        return id;
      } else {
        const res = await api.post<{ success: boolean; data: { id: string } }>('/purchase-orders', payload);
        return res.data.id;
      }
    } catch (err) {
      console.error('[v0] Error saving order:', err);
      setError('Error en guardar la comanda');
      return null;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const savedId = await saveOrder();
    setSaving(false);
    if (savedId) {
      localStorage.setItem('compras-activeTab', 'orders');
      navigate('/compres');
    }
  };

  // Accions de la comanda ja creada (Imprimir/Descarregar/Enviar/Marcar com
  // enviada) — mateixos endpoints que PurchaseOrderDetail.tsx, perque
  // aquestes accions tambe han d'estar disponibles sense sortir de l'edicio.
  const handlePrint = async () => {
    if (!id) return;
    try {
      const response = await apiClient.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data as Blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      console.error('[v0] Error printing PDF:', err);
      setError('Error en obrir el PDF');
    }
  };

  const handleDownloadPDF = async () => {
    if (!id) return;
    try {
      const response = await apiClient.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comanda-${id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[v0] Error downloading PDF:', err);
      setError('Error en descarregar el PDF');
    }
  };

  // Sol·licitar pressupost — envia la comanda (en format "Sol·licitud de
  // preus", sense preus) a 1 o mes proveïdors, SENSE canviar l'estat de la
  // comanda (es queda en Esborrany fins que es Confirma amb el proveïdor
  // i preus definitius).
  const [showRfqDialog, setShowRfqDialog] = useState(false);
  const [rfqSupplierIds, setRfqSupplierIds] = useState<string[]>([]);
  const [sendingRfq, setSendingRfq] = useState(false);
  const [rfqSupplierSearch, setRfqSupplierSearch] = useState('');
  // Segmentació per activitat — mateix criteri que PurchaseOrderDetail.tsx:
  // nomes proveïdors de la mateixa activitat que el proveïdor triat a la
  // comanda (o tots si aquest no en té cap).
  const [includeEsporadic, setIncludeEsporadic] = useState(false);
  const rfqActivitatId = selectedSupplier?.activitatId ?? null;
  const rfqActivityName = selectedSupplier?.activitat?.name ?? null;
  const rfqEligibleSuppliers = suppliers.filter((s) => {
    if (!rfqActivitatId) return true;
    if (s.activitatId !== rfqActivitatId) return false;
    if (!includeEsporadic && s.supplierType === 'ESPORADIC') return false;
    return true;
  });
  const openRfqDialog = () => {
    setRfqSupplierIds(formData.supplierId ? [formData.supplierId] : []);
    setRfqSupplierSearch('');
    setShowRfqDialog(true);
  };
  const toggleRfqSupplier = (supplierId: string) => {
    setRfqSupplierIds((prev) => prev.includes(supplierId) ? prev.filter((s) => s !== supplierId) : [...prev, supplierId]);
  };
  const handleSendRfq = async () => {
    if (!id || rfqSupplierIds.length === 0) return;
    setSendingRfq(true);
    try {
      // Es desa primer perque la sol·licitud ha de reflectir les línies
      // que l'usuari acaba d'editar, no les de l'ultim desat.
      const savedId = await saveOrder();
      if (!savedId) return;
      await api.post(`/purchase-orders/${savedId}/email`, { supplierIds: rfqSupplierIds, mode: 'rfq' });
      setShowRfqDialog(false);
    } catch (err) {
      console.error('[v0] Error sending RFQ:', err);
      setError('No s\'ha pogut enviar la sol·licitud de pressupost');
    } finally {
      setSendingRfq(false);
    }
  };

  // Confirmar pedido — envia la Comanda definitiva (amb els preus reals) al
  // proveïdor ja triat al formulari, i marca la comanda com Enviada, en un
  // sol clic (abans calia "Enviar per correu" + "Marcar com enviada" per
  // separat, i cap dels dos garantia que l'altre s'hagués fet).
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const handleConfirmOrder = async () => {
    if (!id || !formData.supplierId) return;
    if (!window.confirm('Confirmar el pedido desarà els canvis, enviarà la comanda per correu al proveïdor i la marcarà com Enviada. Continuar?')) return;
    setConfirmingOrder(true);
    try {
      // Es desa primer perque el PDF/correu ha de reflectir les dades que
      // l'usuari acaba d'editar (proveïdor/preus definitius), no les de
      // l'ultim desat.
      const savedId = await saveOrder();
      if (!savedId) return;
      await api.post(`/purchase-orders/${savedId}/email`, { supplierIds: [formData.supplierId], mode: 'order' });
      await api.post(`/purchase-orders/${savedId}/submit`, {});
      navigate(`/compres/comandes/${savedId}`);
    } catch (err) {
      console.error('[v0] Error confirming order:', err);
      setError('No s\'ha pogut confirmar el pedido');
    } finally {
      setConfirmingOrder(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Capçalera sticky amb botons d'acció sempre visibles */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b flex items-center justify-between gap-4 py-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/compres')}
            className="p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-foreground truncate">
              {id ? 'Editar Comanda' : 'Nova Comanda de Compra'}
            </h1>
            <p className="text-xs text-muted-foreground">
              {id ? 'Modifica les dades de la comanda' : 'Crea una nova comanda de compra'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate('/compres')}
            className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
          >
            Cancel·lar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !formData.supplierId}
            className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Desant...' : 'Desa Comanda'}
          </button>
        </div>
      </div>

      {/* Accions de la comanda ja creada — nomes en editar (calen l'id i el
          PDF ja generat, no te sentit en una comanda encara sense desar). */}
      {id && (
        <div className="flex gap-2 flex-wrap -mt-2">
          <Button onClick={handlePrint} variant="outline" size="sm" className="gap-2">
            <Printer className="w-4 h-4" />
            Imprimir
          </Button>
          <Button onClick={handleDownloadPDF} variant="outline" size="sm" className="gap-2">
            <Download className="w-4 h-4" />
            Descarregar PDF
          </Button>
          <Button onClick={openRfqDialog} variant="outline" size="sm" className="gap-2">
            <Mail className="w-4 h-4" />
            Sol·licitar pressupost
          </Button>
          <Button
            onClick={handleConfirmOrder}
            disabled={confirmingOrder || !formData.supplierId}
            title={!formData.supplierId ? 'Cal seleccionar un proveïdor abans de confirmar' : undefined}
            size="sm"
            className="gap-2"
          >
            <Send className="w-4 h-4" />
            Confirmar comanda
          </Button>
        </div>
      )}

      {/* Diàleg "Sol·licitar pressupost" — selecciona 1 o mes proveïdors als
          quals enviar la sol·licitud de preus (sense canviar l'estat). */}
      {showRfqDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowRfqDialog(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b">
              <h2 className="text-sm font-semibold">Sol·licitar pressupost</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Selecciona els proveïdors als quals enviar la sol·licitud de preus</p>
              <Input
                autoFocus
                value={rfqSupplierSearch}
                onChange={(e) => setRfqSupplierSearch(e.target.value)}
                placeholder="Cercar proveïdor..."
                className="mt-2"
              />
            </div>
            {/* Capçalera d'activitat i checkbox esporàdics — mateix patró que
                PurchaseOrderDetail.tsx */}
            <div className="flex items-center justify-between gap-4 px-5 pt-3">
              {rfqActivityName && rfqActivitatId ? (
                <p className="text-xs text-muted-foreground">
                  Proveïdors de l&apos;activitat: <span className="font-semibold text-foreground">{rfqActivityName}</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">Tots els proveïdors</p>
              )}
              {rfqActivitatId && (
                <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={includeEsporadic}
                    onChange={(e) => setIncludeEsporadic(e.target.checked)}
                    className="rounded"
                  />
                  Incloure esporàdics
                </label>
              )}
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1">
              {rfqEligibleSuppliers
                .filter((s) => (s.businessName || s.name).toLowerCase().includes(rfqSupplierSearch.trim().toLowerCase()))
                .map((s) => (
                <label key={s.id} className="flex items-center gap-2 py-1.5 text-xs cursor-pointer hover:bg-muted/50 rounded px-1">
                  <input
                    type="checkbox"
                    checked={rfqSupplierIds.includes(s.id)}
                    onChange={() => toggleRfqSupplier(s.id)}
                  />
                  <span className="flex-1">{s.businessName || s.name}</span>
                  {!s.email && <span className="text-[10px] text-amber-600">sense email</span>}
                </label>
              ))}
            </div>
            <div className="px-5 py-4 border-t flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowRfqDialog(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50"
              >
                Cancel·lar
              </button>
              <Button onClick={handleSendRfq} disabled={sendingRfq || rfqSupplierIds.length === 0} size="sm" className="gap-2">
                <Mail className="w-4 h-4" />
                {sendingRfq ? 'Enviant...' : 'Enviar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Main content */}
      <div className="space-y-6">
        {/* 2-column grid — 2 cards */}
        <div className="grid grid-cols-2 gap-6">

          {/* Targeta esquerra: Proveïdor + Marca + Tramesa + Lliurament */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-5 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Building2 size={16} className="text-blue-600" /> Proveïdor
              </h2>
            </div>
            <div className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Selecciona Proveïdor *</label>
                  <div className="relative">
                    <Input
                      ref={supplierInputRef}
                      value={supplierOpen ? supplierSearch : (selectedSupplier ? (selectedSupplier.businessName || selectedSupplier.name) : '')}
                      onChange={(e) => { setSupplierSearch(e.target.value); setSupplierOpen(true); }}
                      onFocus={() => { setSupplierSearch(''); setSupplierOpen(true); }}
                      onBlur={() => setTimeout(() => setSupplierOpen(false), 150)}
                      placeholder="Cercar proveïdor..."
                      className="w-full"
                    />
                    {supplierOpen && (
                      <div className="absolute z-50 w-full bg-white border border-border rounded-md shadow-lg max-h-56 overflow-y-auto mt-1">
                        {suppliers
                          .filter(s => {
                            if (!supplierSearch) return true;
                            const q = supplierSearch.toLowerCase();
                            return (s.businessName || s.name).toLowerCase().includes(q)
                              || (s.tradeName || '').toLowerCase().includes(q);
                          })
                          .map(s => (
                            <div
                              key={s.id}
                              className="px-3 py-2 cursor-pointer hover:bg-accent text-sm"
                              onMouseDown={() => { handleSupplierChange(s.id); setSupplierOpen(false); setSupplierSearch(''); }}
                            >
                              <span>{s.businessName || s.name}</span>
                              {s.tradeName && s.tradeName !== (s.businessName || s.name) && (
                                <span className="ml-2 text-xs text-muted-foreground">{s.tradeName}</span>
                              )}
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Marca (Opcional)</label>
                    <Select
                      value={formData.brandId}
                      onChange={(e) => setFormData(prev => ({ ...prev, brandId: e.target.value }))}
                    >
                      <option value="">Cap marca seleccionada</option>
                      {brands.map(b => (
                        <option key={b.id} value={b.id}>{b.code} - {b.name}</option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Data de Comanda</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                      <Input
                        type="date"
                        value={formData.orderDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, orderDate: e.target.value }))}
                        className="w-full pl-9"
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Forma de Tramesa</label>
                    <Select
                      value={formData.shippingMethod}
                      onChange={(e) => setFormData(prev => ({ ...prev, shippingMethod: e.target.value }))}
                    >
                      <option value="">Selecciona...</option>
                      {SHIPPING_METHODS.map((method) => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Termini de Lliurament</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                      <Input
                        type="date"
                        value={formData.expectedDeliveryDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                        className="w-44 pl-9"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Lloc de Lliurament</label>
                  <Input
                    value={formData.deliveryLocation}
                    onChange={(e) => setFormData(prev => ({ ...prev, deliveryLocation: e.target.value }))}
                  />
                </div>
            </div>
          </div>

          {/* Targeta dreta: OT + Pagament (mateixa fila) + Persona + Observacions */}
          <div className="bg-white border border-gray-200 rounded-lg">
            <div className="px-5 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <CreditCard size={16} className="text-green-600" /> Condicions de la Comanda
              </h2>
            </div>
            <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">OT (Opcional)</label>
                    <Select
                      value={formData.workOrderId}
                      onChange={(e) => handleWorkOrderChange(e.target.value)}
                    >
                      <option value="">Cap OT seleccionada</option>
                      {workOrders
                        .filter(wo => wo.status !== 'completed' && wo.status !== 'cancelled')
                        .map(wo => {
                          const exp = wo.expedient_number ? `EXP-${wo.expedient_number}` : 'OT';
                          const label = `${exp} · ${wo.order_number} — ${wo.name || wo.type || 'Sin nom'}`;
                          return (
                            <option key={wo.id} value={wo.id}>{label}</option>
                          );
                        })}
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Forma de Pagament</label>
                    <Select
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                    >
                      <option value="">Seleccionar...</option>
                      {/* Valor antic no present al catàleg actual — es mostra tal qual
                          en lloc d'amagar-lo. */}
                      {formData.paymentMethod && !metodesPagament.some(m => m.name === formData.paymentMethod) && (
                        <option value={formData.paymentMethod}>{formData.paymentMethod}</option>
                      )}
                      {metodesPagament.map(m => (
                        <option key={m.id} value={m.name}>{m.name}</option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">N/Referència</label>
                  <Select
                    value={formData.referenceUserId}
                    onChange={(e) => setFormData(prev => ({ ...prev, referenceUserId: e.target.value }))}
                  >
                    <option value="">Selecciona la persona que fa la comanda...</option>
                    {companyUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Persona</label>
                  {supplierContacts.length > 0 ? (
                    <select
                      value={formData.persona}
                      onChange={(e) => setFormData(prev => ({ ...prev, persona: e.target.value }))}
                      className="w-full px-3 py-2 border border-input rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Selecciona contacte...</option>
                      {supplierContacts.map((c, i) => (
                        <option key={i} value={c.name}>
                          {c.name}{c.position ? ` — ${c.position}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      value={formData.persona}
                      onChange={(e) => setFormData(prev => ({ ...prev, persona: e.target.value }))}
                      placeholder="Nom del contacte"
                    />
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Observacions</label>
                  <Input
                    value={formData.observations}
                    onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                  />
                </div>
            </div>
          </div>

        </div>

        {/* Lines Card - Full Width */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <FileText size={16} className="text-purple-600" /> Línies de la Comanda
            </h2>
            <Button size="sm" onClick={addLine} variant="outline">
              <Plus className="h-4 w-4 mr-1" />
              Afegir Línia
            </Button>
          </div>
          <div className="p-4 overflow-x-auto">
            <table className="w-full text-xs" style={{ minWidth: 960 }}>
              <thead>
                <tr className="border-b-2 border-border bg-muted/60">
                  <th className="w-6 py-2 px-1"></th>
                  <th className="text-left py-2 px-1 font-semibold text-muted-foreground">Descripció</th>
                  <th className="w-24 text-right py-2 px-1 font-semibold text-muted-foreground">Ut.</th>
                  <th className="w-20 text-right py-2 px-1 font-semibold text-muted-foreground">Amidament</th>
                  <th className="w-28 text-right py-2 px-1 font-semibold text-muted-foreground">Preu de compra</th>
                  <th className="w-20 text-right py-2 px-1 font-semibold text-muted-foreground">Pes Kg</th>
                  <th className="w-20 text-right py-2 px-1 font-semibold text-muted-foreground">Preu/Ut</th>
                  <th className="w-14 text-right py-2 px-1 font-semibold text-muted-foreground">Desc.</th>
                  <th className="w-20 text-right py-2 px-1 font-semibold text-muted-foreground">Import</th>
                  <th className="w-7 py-2 px-1"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/40">

                    {/* Link icon */}
                    <td className="py-1 px-1 text-center">
                      {line.materialId ? (
                        <a
                          href={`/compres/materials/${line.materialId}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Obrir detall del material"
                          className="text-muted-foreground hover:text-blue-600"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : <span className="inline-block w-3" />}
                    </td>

                    {/* Descripció — cerca un material del catàleg o s'hi escriu text lliure */}
                    <td className="py-1 px-1">
                      <div className="relative">
                        <input
                          value={lineOpenIdx === index
                            ? (lineSearches[index] ?? '')
                            : (line.materialId
                                ? (() => { const m = materialCache[line.materialId!]; return m ? m.name : line.description; })()
                                : line.description)}
                          onChange={(e) => {
                            const q = e.target.value;
                            setLineSearches(prev => ({ ...prev, [index]: q }));
                            setLineOpenIdx(index);
                            updateLine(index, 'description', q);
                            if (line.materialId) {
                              updateLine(index, 'materialId', undefined);
                              updateLine(index, 'purchaseCostUnit', '');
                              updateLine(index, 'elementMeasurementFactor', 1);
                              updateLine(index, 'elementPriceFactor', 1);
                              updateLine(index, 'weightKgPerUnit', 0);
                            }
                            if (materialSearchTimer.current) clearTimeout(materialSearchTimer.current);
                            materialSearchTimer.current = setTimeout(() => searchMaterials(q), 200);
                          }}
                          onFocus={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setLineDropdownPos({ top: rect.top, bottom: rect.bottom, left: rect.left, width: Math.max(rect.width, 360) });
                            const currentName = line.materialId
                              ? (materialCache[line.materialId]?.name ?? line.description)
                              : line.description;
                            const q = lineSearches[index] ?? currentName;
                            setLineSearches(prev => ({ ...prev, [index]: q }));
                            setLineOpenIdx(index);
                            searchMaterials(q);
                          }}
                          onBlur={() => setTimeout(() => setLineOpenIdx(null), 150)}
                          placeholder="Cercar material o escriure text lliure..."
                          className="w-full h-7 text-xs border border-input rounded px-1 bg-white focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                    </td>

                    {/* Ut. — quantity */}
                    <td className="py-1 px-1">
                      {numInput(index, 'quantity')}
                    </td>

                    {/* Amidament — Ut. × F.Amidament de l'element (informa la magnitud,
                        no editable: es derivat automaticament de la Ut. i l'element). */}
                    <td className="py-1 px-1 text-right whitespace-nowrap">
                      {fmt2(Number(line.measurementFactor))} <span className="text-muted-foreground">{line.unitType?.toLowerCase()}</span>
                    </td>

                    {/* Preu de compra */}
                    <td className="py-1 px-1">
                      <div className="flex items-center gap-0.5">
                        {numInput(index, 'purchasePrice')}
                        {line.purchaseCostUnit && (
                          <span className="shrink-0 text-muted-foreground ml-0.5">€/{line.purchaseCostUnit.toLowerCase()}</span>
                        )}
                      </div>
                    </td>

                    {/* Pes Kg — si hi ha un material amb pes conegut es calcula (no editable);
                        si no, es pot introduir manualment (p.ex. linies de text lliure). */}
                    <td className="py-1 px-1 text-right whitespace-nowrap">
                      {Number(line.weightKgPerUnit) > 0 ? fmt2(line.weightKg) : numInput(index, 'weightKg')}
                    </td>

                    {/* Preu/Ut — F.Amidament × Factor Preu/UA × Cost Compra de l'element (no editable) */}
                    <td className="py-1 px-1 text-right whitespace-nowrap">
                      {fmt2(Number(line.elementMeasurementFactor) * Number(line.elementPriceFactor) * Number(line.purchasePrice))} €
                    </td>

                    {/* Desc. % */}
                    <td className="py-1 px-1">
                      {numInput(index, 'discountPercent')}
                    </td>

                    {/* Import */}
                    <td className="py-1 px-1 text-right font-semibold whitespace-nowrap">
                      {formatCurrency(line.lineTotal)}
                    </td>

                    {/* Delete */}
                    <td className="py-1 px-1 text-center">
                      <button
                        onClick={() => removeLine(index)}
                        className="p-1 rounded hover:bg-gray-100 text-muted-foreground hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {lines.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-border bg-muted/60 font-semibold">
                    <td colSpan={5} className="py-2 px-1 text-right text-muted-foreground">Totals:</td>
                    <td className="py-2 px-1 text-right whitespace-nowrap">
                      {totals.totalWeight.toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Kg
                    </td>
                    <td />
                    <td />
                    <td className="py-2 px-1 text-right whitespace-nowrap">{formatCurrency(totals.subtotal)}</td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Totals Card - Full width */}
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Calculator size={16} className="text-blue-600" /> Totals
            </h2>
          </div>
          <div className="p-4">
            <div className="flex justify-end">
              <div className="w-72 space-y-2">
                <div className="flex justify-between items-center py-1.5 px-3 bg-muted/50 rounded text-sm">
                  <span className="text-muted-foreground">IVA ({formData.taxRate}%)</span>
                  <span className="font-medium">{formatCurrency(totals.taxAmount)}</span>
                </div>
                <div className="flex justify-between items-center py-2 px-3 bg-primary/10 rounded border border-primary/20">
                  <span className="font-bold text-primary">Total Comanda</span>
                  <span className="font-bold text-primary text-base">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Material autocomplete dropdown — fixed positioning to escape table overflow clipping;
          s'obre cap amunt si no hi ha prou espai per sota (evita que quedi tallat pel final de pantalla) */}
      {lineOpenIdx !== null && lineDropdownPos && (
        <div
          style={{
            position: 'fixed',
            ...(lineDropdownPos.bottom + 224 > window.innerHeight
              ? { bottom: window.innerHeight - lineDropdownPos.top + 2 }
              : { top: lineDropdownPos.bottom + 2 }),
            left: lineDropdownPos.left,
            width: lineDropdownPos.width,
            zIndex: 9999,
          }}
          className="bg-white border border-border rounded-md shadow-xl max-h-56 overflow-y-auto"
        >
          {materialSuggestions.map(m => (
              <div
                key={m.id}
                className="px-3 py-2 cursor-pointer hover:bg-accent text-sm"
                onMouseDown={() => {
                  const idx = lineOpenIdx!;
                  updateLine(idx, 'materialId', m.id);
                  updateLine(idx, 'description', m.name);
                  updateLine(idx, 'unitType', m.unit || 'Ut');
                  updateLine(idx, 'element', parseInt(m.code) || 0);
                  updateLine(idx, 'weightKgPerUnit', m.weightKgPerUnit ?? 0);
                  updateLine(idx, 'elementMeasurementFactor', m.measurementFactor ?? 1);
                  updateLine(idx, 'elementPriceFactor', m.priceFactor ?? 1);
                  // Amidament = Ut. actual × F.Amidament de l'element (ja inclou Ut.)
                  updateLine(idx, 'measurementFactor', (Number(lines[idx]?.quantity) || 1) * (m.measurementFactor ?? 1));
                  updateLine(idx, 'purchasePrice', m.purchaseCost ?? 0);
                  updateLine(idx, 'purchaseCostUnit', m.purchaseCostUnit ?? '');
                  setLineOpenIdx(null);
                  setLineDropdownPos(null);
                  setLineSearches(prev => { const next = { ...prev }; delete next[idx]; return next; });
                }}
              >
                {m.name}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}
